import {
  DialogTrigger,
  Grid,
  GridItem,
  Link,
  mq,
  RangeGraph,
  Text,
} from "@certn/cascadia-react";
import { RenderContext } from "@certn/cascadia-react/context/RenderContext";
import { getTextStyle } from "@certn/cascadia-react/foundations/typography/styles";
import { css } from "@emotion/css";
import { ControlProps } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { CreditScoreModal } from "./CreditScoreModal";

export function CreditScore(props: ControlProps) {
  const { t } = useTranslation();
  const isPDF = useContext(RenderContext).isForPdfGeneration;
  const options = props.uischema.options || {};

  return (
    <div className={styles.container}>
      <Grid form={false} layout="report-tiles" className={styles.grid}>
        <GridItem span={[12, 6]} className={styles.col1}>
          <Text className={styles.name} testID="equifax-credit-score">
            {t("Equifax credit score")}: {options.score || "--"}
          </Text>
          <Text className={styles.value}>
            {t(
              "Credit scores typically range from 300 to 900. Within that range, scores can usually be placed into one of five categories."
            )}
            &nbsp;
            {!isPDF && (
              <DialogTrigger>
                <Link textColor="text-weak" testID="credit-score-learn-more">
                  {t("Learn more")}
                </Link>
                <CreditScoreModal />
              </DialogTrigger>
            )}
          </Text>
        </GridItem>
        <GridItem span={[12, 6]} className={styles.col2}>
          <div>
            <RangeGraph
              config={{
                minRange: 300,
                maxRange: 900,
                ranges: [
                  {
                    min: 300,
                    max: 579,
                    backgroundColor: "var(--tofino-1000)",
                  },
                  {
                    min: 580,
                    max: 669,
                    backgroundColor: "var(--tofino-800)",
                  },
                  {
                    min: 670,
                    max: 739,
                    backgroundColor: "var(--tofino-600)",
                  },
                  {
                    min: 740,
                    max: 799,
                    backgroundColor: "var(--tofino-400)",
                  },
                  {
                    min: 800,
                    max: 900,
                    backgroundColor: "var(--tofino-200)",
                  },
                ],
              }}
              score={options.score}
            />
          </div>
        </GridItem>
      </Grid>
    </div>
  );
}

const styles = {
  container: css({
    border: "1px solid var(--border-base)",
    borderRadius: "var(--border-radius-4)",
    backgroundColor: "var(--background-base)",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  }),
  grid: css({
    columnGap: "var(--space-3)",
    rowGap: "var(--space-3)",
  }),
  col1: css({
    padding: "var(--space-4)",
  }),
  col2: css(
    mq({
      textAlign: ["center", "right"],
    }),
    {
      padding: "var(--space-4)",
      justifyContent: "center",
    }
  ),
  name: css(getTextStyle("global/heading-medium"), {}),
  value: css(getTextStyle("global/body-primary"), {
    color: "var(--text-weak)",
  }),
  link: css(getTextStyle("global/body-primary-link"), {
    color: "var(--text-weak)",
  }),
};

export const CreditScoreRenderer = withJsonFormsControlProps(CreditScore);
